import { generatePath, type Location, matchPath } from 'react-router'

import { type UserSessionTeam } from 'types/api/users/sessions/user-session'

/**
 * Constants
 */
export const MAIN_HEADER_MENU_WIDTH = 320
const ROUTES_PRESERVING_FULL_PATHNAME = ['/reports/*']
const ROUTES_PRESERVING_BASE_PATHNAME = [
  '/settings/team/:teamId/*',
  '/configurations/:featureName/*'
]

/**
 * Derives the proper level of route preservation when switching teams.
 */
export function getTeamSwitchRedirectUrl(
  team: UserSessionTeam,
  location: Location
) {
  /*
   * Preserve full pathname on these routes
   * Only redirects if a match has been found
   */
  for (const route of ROUTES_PRESERVING_FULL_PATHNAME) {
    const { pathname } = matchPath(route, location.pathname) ?? {}

    if (pathname) return pathname
  }

  /*
   * Redirect to the base pathname on these routes. This includes only the segments that are matched
   * by the provided route (first argument of matchPath). Again, only redirects if a match has been found.
   */
  for (const route of ROUTES_PRESERVING_BASE_PATHNAME) {
    const { params, pathnameBase } = matchPath(route, location.pathname) ?? {}
    const { teamId } = params ?? {}

    // If we can identify the route contains a :teamId param, replace it with the new team identifier
    if (teamId) {
      const pathnameWithNewTeamId = generatePath(route, {
        ...params,
        teamId: team.identifier
      })

      /*
       * After we generate the new full location pathname, we should truncate this to the desired base pathname.
       * This base pathname is defined by the route defined in the ROUTES_PRESERVING_BASE_PATHNAME array.
       */
      const { pathnameBase } = matchPath(route, pathnameWithNewTeamId) ?? {}

      if (pathnameBase) return pathnameBase
    }

    // Simple truncation to base pathname in the case no :teamId param is found
    if (pathnameBase) return pathnameBase
  }

  // In all other cases, redirect to the root of the current app feature, or the index page as a fallback.
  const { pathnameBase = '/' } =
    matchPath('/:featureName/*', location.pathname) ?? {}

  return pathnameBase
}
